/**
 * EditorialCalendarSection — Sprint 6 · Project Symphony
 *
 * The editorial calendar. Two views (List / Calendar) — the executive
 * directive's Timeline view lands in Sprint 6.5. Every slot displays
 * the strategic advisor "reason" sentence — Orkexra explains why
 * Tuesday and not Monday.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Card } from "@/shared/ui/card";
import { approveEditorial, getEditorial, planEditorial } from "../api";
import type { EditorialSchedule, EditorialSlot } from "../api";

type ViewMode = "list" | "calendar";

export function EditorialCalendarSection({
  blueprintId,
  campaignStatusCode,
}: {
  blueprintId: string;
  campaignStatusCode: string;
}): JSX.Element | null {
  const qc = useQueryClient();
  const [view, setView] = useState<ViewMode>("calendar");

  const enabled =
    campaignStatusCode === "generation_complete" ||
    campaignStatusCode === "scheduling" ||
    campaignStatusCode === "publishing" ||
    campaignStatusCode === "active";

  const query = useQuery({
    queryKey: ["editorial", blueprintId],
    queryFn: () => getEditorial(blueprintId),
    enabled: enabled && Boolean(blueprintId),
    retry: 0,
  });

  const replan = useMutation({
    mutationFn: () => planEditorial(blueprintId),
    onSuccess: (r) => qc.setQueryData(["editorial", blueprintId], r),
  });

  const approve = useMutation({
    mutationFn: () => approveEditorial(blueprintId),
    onSuccess: (r) => {
      qc.setQueryData(["editorial", blueprintId], r);
      qc.invalidateQueries({ queryKey: ["campaign-room", blueprintId] });
    },
  });

  if (!enabled) return null;

  if (query.isLoading) {
    return (
      <section data-testid="room-editorial-loading" className="flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Editorial calendar is being planned…
      </section>
    );
  }

  const sched = query.data as EditorialSchedule | undefined;
  if (!sched) return null;

  const isApproved = sched.status === "approved";
  const h = sched.editorial_health;

  return (
    <section data-testid="room-editorial-calendar" className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Editorial Calendar
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          Your department has planned this campaign.
        </h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Every date is intentional. Every channel has a purpose. The Editorial
          Planner chose these slots to serve your campaign objective — not to
          fill a calendar.
        </p>
      </div>

      {/* Editorial Health */}
      <Card className="p-6" data-testid="editorial-health-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              Editorial Health
            </p>
            <div className="flex items-baseline gap-3">
              <span className="text-2xl" aria-hidden>{h.indicator}</span>
              <span className="text-2xl sm:text-3xl font-semibold" data-testid="editorial-health-label">
                {h.label}
              </span>
              <span className="text-sm text-muted-foreground">· {h.score}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Campaign balance {h.campaign_balance_pct}% · Audience fatigue {h.audience_fatigue} · Coverage {h.coverage_pct}%
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={view === "calendar" ? "default" : "outline"}
              size="sm"
              onClick={() => setView("calendar")}
              data-testid="editorial-view-calendar"
            >Calendar</Button>
            <Button
              variant={view === "list" ? "default" : "outline"}
              size="sm"
              onClick={() => setView("list")}
              data-testid="editorial-view-list"
            >List</Button>
          </div>
        </div>
        {sched.conflicts.length > 0 && (
          <ul className="mt-4 space-y-1 text-sm" data-testid="editorial-conflicts">
            {sched.conflicts.map((c, i) => (
              <li key={i} className={c.severity === "high" ? "text-rose-600" : "text-amber-600"}>
                — {c.message}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Slots */}
      {view === "list" ? (
        <SlotList slots={sched.slots} />
      ) : (
        <SlotCalendar slots={sched.slots} />
      )}

      {/* Approval bar */}
      <Card className="p-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <p className="text-sm font-medium">
            {isApproved
              ? "Editorial calendar approved. The Publishing Team is ready."
              : "Approve the editorial calendar to hand off to the Publishing Team."}
          </p>
          <p className="text-xs text-muted-foreground">
            {isApproved
              ? "Sprint 6 ends here. Publishing itself begins later."
              : "This is separate from asset approval — it's your review of the whole campaign shape."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => replan.mutate()}
            disabled={isApproved || replan.isPending}
            data-testid="editorial-replan-button"
          >
            {replan.isPending ? "Re-planning…" : "Re-plan"}
          </Button>
          {isApproved ? (
            <Button disabled data-testid="editorial-approved-badge">
              <CheckCircle2 className="h-4 w-4 mr-2" aria-hidden />
              Approved
            </Button>
          ) : (
            <Button
              onClick={() => approve.mutate()}
              disabled={approve.isPending}
              data-testid="editorial-approve-button"
            >
              {approve.isPending ? "Approving…" : "Approve Editorial Calendar"}
            </Button>
          )}
        </div>
      </Card>

      {/* Patch 37.4.9 — post-approval "Where do I go next?" affordance.
          Answers the founder's question: once the calendar is Approved,
          the Publishing Team is ready and the founder should be one tap
          away from the Publishing calendar. */}
      {isApproved ? (
        <Card
          className="border-emerald-500/30 bg-emerald-500/5 p-5 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="editorial-next-step"
        >
          <div className="flex-1">
            <p className="text-sm font-medium">Next step — review the Publishing calendar</p>
            <p className="text-xs text-muted-foreground">
              Each approved slot has been queued for the Publishing Team. Open the
              Publishing calendar to confirm channels, connect accounts, and let
              posts go out on schedule.
            </p>
          </div>
          <Button asChild data-testid="editorial-open-publishing">
            <Link to={`/publishing?campaign=${encodeURIComponent(blueprintId)}`}>
              Open Publishing calendar
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </Card>
      ) : null}
    </section>
  );
}

function SlotList({ slots }: { slots: EditorialSlot[] }): JSX.Element {
  return (
    <Card className="p-4 sm:p-6" data-testid="editorial-list-view">
      <ul className="divide-y divide-border">
        {slots.map((s) => (
          <li key={s.slot_id} className="py-4" data-testid={`editorial-slot-${s.slot_id}`}>
            <div className="flex flex-wrap items-baseline gap-3 mb-1">
              <span className="text-sm font-mono text-muted-foreground tabular-nums">
                {new Date(s.publish_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                {" · "}
                {new Date(s.publish_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
              </span>
              <Badge variant="outline">{s.channel_label}</Badge>
              <Badge variant="secondary">{s.theme_label}</Badge>
            </div>
            <p className="text-sm">{s.reason}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SlotCalendar({ slots }: { slots: EditorialSlot[] }): JSX.Element {
  // Group slots by ISO week
  const byWeek: Record<string, EditorialSlot[]> = {};
  for (const s of slots) {
    const d = new Date(s.publish_at);
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const key = monday.toISOString().slice(0, 10);
    (byWeek[key] ||= []).push(s);
  }
  const weeks = Object.keys(byWeek).sort();

  return (
    <div className="space-y-4" data-testid="editorial-calendar-view">
      {weeks.map((weekStart) => (
        <Card key={weekStart} className="p-4 sm:p-6" data-testid={`editorial-week-${weekStart}`}>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Week of {new Date(weekStart).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </p>
          <ul className="space-y-3">
            {byWeek[weekStart].map((s) => (
              <li
                key={s.slot_id}
                className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4"
                data-testid={`editorial-slot-${s.slot_id}`}
              >
                <span className="text-sm font-semibold sm:w-32 shrink-0">
                  {s.day_label}
                </span>
                <span className="text-sm text-muted-foreground sm:w-40 shrink-0">
                  {new Date(s.publish_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                  {" · "}
                  {s.channel_label}
                </span>
                <span className="text-sm flex-1">
                  <span className="font-medium">{s.theme_label}</span>
                  {" — "}
                  <span className="text-muted-foreground">{s.reason}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
